import { observer } from 'mobx-react';
import React from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import {
    ArrowRight,
    BadgeCheck,
    ShieldAlert,
    ShieldCheck,
    TriangleAlert
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { useTabs } from '../../components/hooks/use-tabs';
import { useWrapRequest } from '../../components/hooks/use-wrap-request';
import { PageTabs } from '../../components/page-tabs';
import { Spinner } from '../../components/spinner';
import { Api } from '../../core/api';
import { UserSearchSupportDto } from '../../core/api/MobileClient';
import {
    UserFlagDto,
    WorkoutFlagStatus
} from '../../core/api/FitnessActivityClient';
import { injectTSDI } from '../../core/tsdi';
import { toast } from '@core/toast';
import { cn } from '@core/utils/cn';

const pageTabs = [
    { label: 'Details', id: 'details' },
    { label: 'Wallet', id: 'wallet' },
    { label: 'Activities', id: 'activities' },
    { label: 'Activity Points', id: 'activity-points' },
    { label: 'Badges', id: 'badges' },
    { label: 'Goals', id: 'goals' },
    { label: 'Fraud Monitor', id: 'flags-tool' },
    { label: 'GPX Explorer', id: 'gpx-explorer' },
    { label: 'Fitness Apps', id: 'fitness-apps' },
    { label: 'Devices', id: 'devices' }
];

const FRAUD_MONITOR_TAB = pageTabs.findIndex((tab) => tab.id === 'flags-tool');

export const UserContext = React.createContext<UserSearchSupportDto>(
    {} as UserSearchSupportDto
);

export const UserTypeContext = React.createContext<{
    userType: UserSearchSupportDto['userType'];
    setUserType(userType: UserSearchSupportDto['userType']): void;
}>({ userType: 'USER', setUserType: () => undefined });

const flagPresentation = {
    [WorkoutFlagStatus.SUSPICIOUS]: {
        title: 'Suspicious',
        variant: 'warning' as const,
        Icon: TriangleAlert
    },
    [WorkoutFlagStatus.FRAUD]: {
        title: 'Fraud',
        variant: 'destructive' as const,
        Icon: ShieldAlert
    },
    [WorkoutFlagStatus.NOT_FRAUD]: {
        title: 'Reviewed · not fraud',
        variant: 'default' as const,
        Icon: ShieldCheck
    }
};

const UserFlagBanner = ({
    userFlag,
    onOpen
}: {
    userFlag: UserFlagDto;
    onOpen(): void;
}) => {
    const { title, variant, Icon } = flagPresentation[userFlag.status];
    return (
        <Alert variant={variant} className="max-w-md">
            <Icon />
            <AlertTitle className="uppercase tracking-wide">{title}</AlertTitle>
            <AlertDescription>
                {userFlag.comment && (
                    <p className="line-clamp-2">{userFlag.comment}</p>
                )}
                <div className="mt-1 flex items-center gap-3 text-xs">
                    <span>Flagged by {userFlag.createdBy || '-'}</span>
                    <Button
                        variant="link"
                        size="xs"
                        className="h-auto p-0"
                        onClick={onOpen}
                    >
                        Open Fraud Monitor <ArrowRight />
                    </Button>
                </div>
            </AlertDescription>
        </Alert>
    );
};

const copyToClipboard = (value: string) => {
    void navigator.clipboard.writeText(value);
    toast.success(`${value} was copied to the clipboard`);
};

const CopyableValue = ({
    value,
    className,
    children
}: {
    value: string;
    className?: string;
    children: React.ReactNode;
}) => (
    <button
        type="button"
        title={`Copy ${value}`}
        className={cn('cursor-pointer hover:underline', className)}
        onClick={() => copyToClipboard(value)}
    >
        {children}
    </button>
);

const initialsOf = (user: UserSearchSupportDto) =>
    `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();

const UserHeader = ({ user }: { user: UserSearchSupportDto }) => {
    const { userType } = React.useContext(UserTypeContext);
    const mainEmail = user.emails.find((email) => email.main) ?? user.emails[0];
    const otherEmails = user.emails.length - (mainEmail ? 1 : 0);
    return (
        <div className="flex items-center gap-5">
            <div className="flex size-16 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-xl font-bold">
                {initialsOf(user)}
            </div>
            <div className="flex min-w-0 flex-col gap-2">
                <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-semibold tracking-tight">
                        {user.firstName} {user.lastName}
                    </h1>
                    <Badge variant="outline">{userType}</Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <CopyableValue
                        value={String(user.id)}
                        className="tabular-nums"
                    >
                        ID {user.id}
                    </CopyableValue>
                    {mainEmail && (
                        <>
                            <span>·</span>
                            <span className="flex items-center gap-1 text-foreground">
                                <CopyableValue value={mainEmail.value}>
                                    {mainEmail.value}
                                </CopyableValue>
                                {mainEmail.verified ? (
                                    <BadgeCheck className="size-4 text-primary" />
                                ) : (
                                    <span className="text-xs text-warning">unverified</span>
                                )}
                            </span>
                        </>
                    )}
                    {otherEmails > 0 && (
                        <span className="text-xs">+{otherEmails} more</span>
                    )}
                    <span>·</span>
                    <CopyableValue value={user.feedId}>
                        Feed ID {user.feedId}
                    </CopyableValue>
                </div>
            </div>
        </div>
    );
};

const UserTypeSync = ({
    user,
    onSync
}: {
    user: UserSearchSupportDto;
    onSync(userType: UserSearchSupportDto['userType']): void;
}) => {
    React.useEffect(() => {
        onSync(user.userType);
    }, [user.id, user.userType]);

    return null;
};

export default observer(() => {
    const { pathname } = useLocation();
    const { tab, getPageLink, setPageTab } = useTabs(pageTabs);
    const navigate = useNavigate();
    const { mobileClient, fitnessActivityClient } = injectTSDI(Api);

    const { userId } = useParams();

    const [userType, setUserType] =
        React.useState<UserSearchSupportDto['userType']>('USER');

    const userRequest = useWrapRequest<UserSearchSupportDto, any>(
        async () => {
            const result =
                await mobileClient.supportApi.userSearchSupportControllerGetUser(
                    Number(userId)
                );

            return result.data;
        },
        {
            deps: []
        }
    );

    const userFlagRequest = useWrapRequest<
        UserFlagDto | undefined,
        [string | undefined]
    >(
        async (userId) => {
            if (userId === undefined) {
                return undefined;
            }

            try {
                const result =
                    await fitnessActivityClient.admin.userFlagControllerGetUserFlags(
                        { userId }
                    );

                return result.data.content[0];
            } catch (error: any) {
                if (error?.status === 404) {
                    return undefined;
                }

                throw error;
            }
        },
        {
            deps: [userId]
        }
    );

    const changePage = (page: number) => {
        navigate(getPageLink(page));
    };

    React.useEffect(() => {
        setPageTab(pathname, 3);
    }, [pathname]);

    return (
        <>
            {userRequest.match({
                loading: () => (
                    <div className="flex min-h-[400px] items-center justify-center">
                        <Spinner />
                    </div>
                ),
                fetched: (user) => (
                    <UserContext.Provider value={user}>
                        <UserTypeContext.Provider
                            value={{ userType, setUserType }}
                        >
                            <UserTypeSync user={user} onSync={setUserType} />
                            <div className="mb-6 flex flex-wrap items-start justify-between gap-6">
                                <UserHeader user={user} />
                                {userFlagRequest.match({
                                    fetched: (userFlag) =>
                                        userFlag ? (
                                            <UserFlagBanner
                                                userFlag={userFlag}
                                                onOpen={() =>
                                                    changePage(FRAUD_MONITOR_TAB)
                                                }
                                            />
                                        ) : null
                                })}
                            </div>
                            <PageTabs
                                tabs={pageTabs}
                                value={tab}
                                onChange={changePage}
                            />
                            <div className="mt-6">
                                <Outlet />
                            </div>
                        </UserTypeContext.Provider>
                    </UserContext.Provider>
                )
            })}
        </>
    );
});
